<template>
  <v-dialog v-model="dialog" width="800px" :fullscreen="$vuetify.display.smAndDown">
    <v-card>
      <!------------------------------------------TOOLBAR------------------------------------------>
      <v-toolbar dark color="grey-darken-4" elevation="0">
        <v-toolbar-title>
          {{ $t("clients.client") }}: {{ clientDetails.name }}
          {{ clientDetails.surname }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          class="ma-3"
          variant="outlined"
          icon
          color="white"
          @click="close"
        >
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </v-toolbar>

      <!-----------------------------------------DATATABLE----------------------------------------->
      <v-row class="ma-0">
        <v-col cols="12">
          <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
          <div class="d-md-none client-sales-mobile-list mt-2 px-2 pb-4">
            <v-progress-linear
              v-if="loading"
              indeterminate
              color="red"
              class="mb-2"
            />

            <div
              v-for="(row, index) in paginatedClientSales"
              :key="row._id || row.sale_id?._id || index"
              class="client-sales-mobile-card"
              :class="{
                'client-sales-mobile-card--selected': index === activeRow,
              }"
              @click="activeRow = activeRow === index ? null : index"
            >
              <div class="client-sales-mobile-card__main">
                <div class="client-sales-mobile-card__info">
                  <div class="client-sales-mobile-card__info-top">
                    <div class="d-flex align-center flex-wrap ga-2">
                      <v-chip
                        v-if="row.sale_id && row.sale_id.status === 'Sold'"
                        label
                        size="small"
                        variant="outlined"
                        color="green"
                      >
                        {{ $t("clients.table1.sold") }}
                      </v-chip>
                      <v-chip
                        v-else
                        label
                        size="small"
                        variant="outlined"
                        color="red"
                      >
                        {{ $t("clients.table1.returned") }}
                      </v-chip>

                      <v-btn
                        v-if="row.sale_id?.date_created"
                        color="black"
                        variant="outlined"
                        size="x-small"
                        readonly
                        @click.stop
                      >
                        {{ formatDate(row.sale_id.date_created) }}
                      </v-btn>
                    </div>

                    <div class="font-weight-bold text-primary text-body-2 mt-2">
                      {{ multilanguage(row.sale_id?.part?.title) }}
                    </div>

                    <div class="text-caption text-grey-darken-1 mt-1">
                      <span class="font-weight-medium text-grey-darken-2">
                        {{ $t("clients.table1.part_id") }}:
                      </span>
                      <span v-if="row.sale_id?.part?.old_id">
                        {{ row.sale_id.part.old_id.slice(-6) }}
                      </span>
                      <span v-else-if="row.sale_id?.part_id">
                        {{ row.sale_id.part_id.slice(-6) }}
                      </span>
                    </div>

                    <div class="text-caption text-grey-darken-1 mt-1">
                      <span class="font-weight-medium text-grey-darken-2">
                        {{ $t("clients.table1.price") }}:
                      </span>
                      <span v-if="row.sale_id?.part?.price">
                        {{ row.sale_id.part.price }}
                      </span>
                      <span v-if="row.sale_id?.part?.currency">
                        {{ row.sale_id.part.currency }}
                      </span>
                    </div>

                    <div class="text-caption text-grey-darken-1 mt-1">
                      <span class="font-weight-medium text-grey-darken-2">
                        {{ $t("clients.table1.debt") }}:
                      </span>
                      <span v-if="row.sale_id?.debt">{{ row.sale_id.debt }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="client-sales-mobile-card__actions">
                <v-spacer />
                <v-btn
                  v-if="row.sale_id"
                  :disabled="
                    row.sale_id.status !== 'Sold' ||
                    !row.sale_id.debt ||
                    row.sale_id.debt < 1
                  "
                  variant="text"
                  icon
                  size="small"
                  @click.stop="
                    saleId = row.sale_id._id;
                    showPayment = true;
                  "
                >
                  <v-icon size="20">mdi-credit-card-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <v-alert
              v-if="!loading && paginatedClientSales.length < 1"
              type="info"
              variant="tonal"
              class="mt-2"
            >
              No data available.
            </v-alert>
          </div>
          <!-- ===================================================================== -->

          <div class="client-sales-table-host pb-2">
          <v-data-table
            v-model:options="pagination"
            :headers="headers"
            :items="clientSales"
            :loading="loading"
            :items-per-page-options="[
              { value: 5, title: '5' },
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
            ]"
            :items-per-page="pagination.itemsPerPage"
          >
            <template #progress>
              <v-progress-linear color="red" indeterminate />
            </template>

            <template #item="{ item, index }">
              <tr>
                <!-- STATUS -->
                <td>
                  <v-chip
                    v-if="item.sale_id && item.sale_id.status === 'Sold'"
                    label
                    variant="outlined"
                    color="green"
                  >
                    {{ $t("clients.table1.sold") }}
                  </v-chip>
                  <v-chip v-else label variant="outlined" color="red">
                    {{ $t("clients.table1.returned") }}
                  </v-chip>
                </td>

                <!-- ID -->
                <td>
                  <span v-if="item.sale_id.part.old_id">
                    {{ item.sale_id.part.old_id.slice(-6) }}
                  </span>
                  <span v-else-if="item.sale_id.part_id">
                    {{ item.sale_id.part_id.slice(-6) }}
                  </span>
                </td>

                <!-- TITLE -->
                <td>
                  <span v-if="item.sale_id.part.title">
                    {{ multilanguage(item.sale_id.part.title) }}
                  </span>
                </td>

                <!-- DATE -->
                <td>
                  <span v-if="item.sale_id.date_created">
                    {{ formatDate(item.sale_id.date_created) }}
                  </span>
                </td>

                <!-- PRICE -->
                <td>
                  <span v-if="item.sale_id.part">
                    <span v-if="item.sale_id.part.price">{{
                      item.sale_id.part.price
                    }}</span>
                    <span v-if="item.sale_id.part.currency">{{
                      item.sale_id.part.currency
                    }}</span>
                  </span>
                </td>

                <!-- DEBT -->
                <td>
                  <span v-if="item.sale_id.debt">{{ item.sale_id.debt }}</span>
                </td>

                <!-- PAYMENT -->
                <td>
                  <v-btn
                    v-if="item.sale_id"
                    :disabled="
                      item.sale_id.status !== 'Sold' ||
                      !item.sale_id.debt ||
                      item.sale_id.debt < 1
                    "
                    variant="text"
                    icon
                    @click="
                      saleId = item.sale_id._id;
                      showPayment = true;
                    "
                  >
                    <v-icon>mdi-credit-card-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
          </div>
        </v-col>
      </v-row>

      <!---------------------------------------MAKE PAYMENT---------------------------------------->
      <v-row v-if="showPayment" align="center" justify="center" class="mt-2">
        <v-col cols="6" sm="4">
          <v-row align="center">
            <v-text-field
              v-model="payment"
              :label="$t('clients.payment')"
              type="number"
              min="0"
              color="red-darken-1"
            />
            <v-btn
              :loading="loadingPayment"
              variant="text"
              icon
              @click="makePayment"
            >
              <v-icon>mdi-check-underline</v-icon>
            </v-btn>
          </v-row>
        </v-col>
      </v-row>

      <!-------------------------------------TOTALS---------------------------------------------->
      <v-row align="center" justify="center" class="mt-2 pb-4 mb-4 client-details-totals">
        <v-col cols="12" sm="auto" class="d-flex justify-center pa-1">
          <v-btn color="black" variant="outlined" class="client-details-total-btn">
            {{ $t("clients.total_expenses") }}:
            {{ clientDetails.total_expenses ? clientDetails.total_expenses.toFixed(2) : "" }}
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto" class="d-flex justify-center pa-1">
          <v-btn color="black" variant="outlined" class="client-details-total-btn">
            {{ $t("clients.total_debts") }}:
            {{ clientDetails.total_debts ? clientDetails.total_debts.toFixed(2) : "" }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import postRequest from "../../api/postRequest";

export default {
  data: () => ({
    dialog: false,
    loading: false,
    loadingPayment: false,
    activeRow: null,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      sortBy: [{ key: "sale_id.date_created", order: "desc" }],
      sortDesc: [true],
    },
    headers: [
      {
        title: "Status",
        sortable: true,
        key: "sale_id.status",
      },
      {
        title: "Part ID",
        sortable: false,
        key: "sale_id.part_id",
      },
      {
        title: "Title",
        sortable: false,
        key: "sale_id.part.title",
      },
      {
        title: "Date of sale",
        sortable: true,
        key: "sale_id.date_created",
      },
      {
        title: "Price",
        sortable: true,
        key: "sale_id.part.price",
      },
      {
        title: "Debt",
        sortable: true,
        key: "sale_id.debt",
      },
      {
        title: "Make Payment",
        sortable: false,
        key: "",
      },
    ],
    clientDetails: {},
    clientSales: [],
    payment: 0,
    saleId: null,
    showPayment: false,
  }),
  computed: {
    paginatedClientSales() {
      const items = [...(this.clientSales || [])];
      const sortBy = this.pagination.sortBy?.[0];
      if (sortBy?.key === "sale_id.date_created") {
        items.sort((a, b) => {
          const da = new Date(a?.sale_id?.date_created || 0).getTime();
          const db = new Date(b?.sale_id?.date_created || 0).getTime();
          return sortBy.order === "desc" ? db - da : da - db;
        });
      }
      const page = this.pagination.page || 1;
      const perPage = this.pagination.itemsPerPage || 5;
      const start = (page - 1) * perPage;
      return items.slice(start, start + perPage);
    },
  },
  watch: {
    dialog(value) {
      if (!value) this.close();
    },
    "$i18n.locale"() {
      this.getLocale();
    },
    pagination: {
      handler() {
        this.activeRow = null;
      },
      deep: true,
    },
  },
  created() {
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("clients.table1.status");
      this.headers[1].title = this.$t("clients.table1.part_id");
      this.headers[2].title = this.$t("clients.table1.title");
      this.headers[3].title = this.$t("clients.table1.sale_date");
      this.headers[4].title = this.$t("clients.table1.price");
      this.headers[5].title = this.$t("clients.table1.debt");
      this.headers[6].title = this.$t("clients.table1.make_payment");
    },
    close() {
      this.dialog = false;
      this.loading = false;
      this.loadingPayment = false;
      this.clientDetails = {};
      this.clientSales = [];
      this.payment = 0;
      this.saleId = null;
      this.showPayment = false;
      this.activeRow = null;
      this.pagination.page = 1;
    },
    open(clientId) {
      this.dialog = true;
      this.loading = true;
      this.pagination.page = 1;
      getRequest("/client/", `id=${clientId}`)
        .then((response) => {
          this.loading = false;
          [this.clientDetails] = response.client;
          this.clientSales =
            this.clientDetails.category &&
            this.clientDetails.category.length > 0
              ? this.clientDetails.category
              : [];
        })
        .catch(() => {
          this.loading = false;
        });
    },
    makePayment() {
      this.loadingPayment = true;
      if (this.saleId !== null) {
        postRequest
          .object(`/client/payment?id=${this.clientDetails._id}`, {
            sale_id: this.saleId,
            payment: this.payment,
          })
          .then((response) => {
            if (response) {
              this.$emit("saved");
              this.close();
            }
          })
          .catch(() => {
            this.close();
          });
      } else {
        this.loadingPayment = false;
        this.showPayment = false;
        this.payment = 0;
      }
    },
    multilanguage(textObject) {
      if (textObject && textObject !== null && typeof textObject === "object") {
        return textObject[this.$store.getters.language];
      }
      return "";
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
  },
};
</script>

<style scoped>
@media (max-width: 959.98px) {
  .client-sales-table-host :deep(.v-table__wrapper),
  .client-sales-table-host :deep(.v-data-table__wrapper),
  .client-sales-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .client-sales-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }

  .client-details-totals {
    width: 100%;
    padding-inline: 12px;
  }

  .client-details-total-btn {
    width: 100%;
    white-space: normal;
    height: auto !important;
    min-height: 36px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .client-details-total-btn :deep(.v-btn__content) {
    white-space: normal;
    line-height: 1.3;
    text-align: center;
  }
}
@media (min-width: 960px) {
  .client-sales-mobile-list {
    display: none !important;
  }
}

.client-sales-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.client-sales-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.client-sales-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;
}
.client-sales-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.client-sales-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 6px;
}
</style>
